import React, { useEffect, useState } from "react";
import { Share2, Copy, Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "./ui/dialog";

export default function ShowcaseDialog() {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/showcase/settings").then((res) => setSettings(res.data)).catch(() => {});
    }
  }, [open]);

  const toggle = async (on) => {
    setBusy(true);
    try {
      const res = await api.post(on ? "/showcase/enable" : "/showcase/disable");
      setSettings(res.data);
      toast.success(on ? "Your showcase link is live" : "Showcase disabled — the link no longer works");
    } catch {
      toast.error("Could not update showcase. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const shareUrl = settings?.token ? `${window.location.origin}/api/showcase/${settings.token}/share` : "";
  const previewUrl = settings?.token ? `${window.location.origin}/showcase/${settings.token}` : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed — select and copy the link manually");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="binder-share-button" variant="outline" className="border-white/20 hover:bg-white/5 rounded-full">
          <Share2 className="w-4 h-4 mr-2" /> Showcase
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="showcase-dialog" className="bg-[#111113] border-white/10 text-white sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">Showcase your binder</DialogTitle>
          <DialogDescription className="text-zinc-400">
            Anyone with the link can browse and filter your cards — view only. They can't change
            anything, and your notes and sale prices stay private.
          </DialogDescription>
        </DialogHeader>
        {settings ? (
          <div className="space-y-4 pt-2">
            <div className="flex items-center justify-between border border-white/10 rounded-xl p-4">
              <div>
                <p className="text-sm font-medium">Public showcase link</p>
                <p className="text-xs text-zinc-500">{settings.enabled ? "Live — anyone with the link can view" : "Off — the link is disabled"}</p>
              </div>
              <Switch data-testid="showcase-toggle" checked={settings.enabled} onCheckedChange={toggle} disabled={busy} />
            </div>
            {settings.enabled && shareUrl && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    data-testid="showcase-link-input"
                    readOnly
                    value={shareUrl}
                    onFocus={(e) => e.target.select()}
                    className="flex-1 bg-black/40 border border-white/10 rounded-full px-4 py-2 text-xs text-zinc-300 focus:outline-none focus:border-[#A78BFA]/50"
                  />
                  <Button data-testid="showcase-copy-button" size="sm" onClick={copyLink} className="bg-[#A78BFA] text-[#05060f] hover:bg-[#8B5CF6] rounded-full shrink-0">
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-[11px] text-zinc-500">
                  Paste it anywhere — Discord, X, Reddit — and it unfurls into a preview card showing your top cards.
                </p>
                <a
                  data-testid="showcase-preview-link"
                  href={previewUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-[#A78BFA] hover:text-white transition-colors"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Preview your showcase
                </a>
              </div>
            )}
          </div>
        ) : (
          <div className="py-6 flex justify-center"><div className="w-6 h-6 border-2 border-white/20 border-t-[#A78BFA] rounded-full animate-spin" /></div>
        )}
      </DialogContent>
    </Dialog>
  );
}
